import React from 'react';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/TypographySupport/TypographySupport';
import { Divider } from '@mui/material';
import CodeContainer from 'js/components/data_sources/add_ds_form/IncomingWebhookConfiguration/CodeContainer';
import { sanitizeApiKey } from 'js/lib/URLUtils';
import useEntities from 'new/state/entities/repository/useEntities';
import { ROUTE_SETTINGS_AUTHENTICATION } from 'new/constants/pathnames';
import { SearchFilterTypes } from 'js/constants/visibilityFilters';
import { Link } from 'react-router-dom';

function getCurl(apiKey, url) {
  return `curl ${url}\n
   -H "Authorization: Basic ${apiKey}"`;
}

const payloadSchema = `{
  "user_prompt": string,    // Required - The query to be processed
  "user_context": {
    "user_id": string,      // Optional - Identifier for the end user
    "access_role": string[],// Optional - User's access roles
    "session_id": string    // Optional - Session identifier
  },
  "llm_config": {          // Required if multiple LLMs have been configured
    "model": string,       // Model name specific to provider
    "provider": string,    // LLM provider (e.g., "openai", "anthropic")
    "credential_id": string// Provider credential ID
  }
}`;

export default function QueryPromptConfiguration(props) {
  const { data } = props;
  const { api_keys } = data;
  const { data: apiKeys } = useEntities('apiKeys');
  const apiKey = apiKeys[api_keys?.[0]] || {};

  const sanitizedApiKey = sanitizeApiKey(apiKey.api_key || '');

  const apiKeyUrl = apiKey?.id
    ? `${ROUTE_SETTINGS_AUTHENTICATION}?search=${SearchFilterTypes.ID}:${apiKey.id}`
    : `${ROUTE_SETTINGS_AUTHENTICATION}`;

  return (
    <Stack paddingBottom="24px" gap="16px">
      <Typography variant="bodyDefault">
        This configuration page provides everything you need to power AI chatbots and intelligent
        search features in your applications using RAG technology. Below you'll find the endpoint
        details and authentication credentials required to start sending queries to our RAG-powered
        API. Once configured, your application can process natural language queries that are
        automatically enriched with context from your connected nexsets.
        <br />
        <br />
        When you send a request to this endpoint, the service will:
        <br />
        <ul>
          <li>Rewrite your query to optimize for context retrieval from your data sources</li>
          <li>Automatically fetch and incorporate relevant data from your connected nexsets</li>
          <li>Return LLM-generated responses that are grounded in your actual data</li>
        </ul>
        The endpoint accepts POST requests with JSON payloads containing your query and optional
        parameters. Use the configuration details below to set up your integration.
      </Typography>
      <Divider />
      <Typography variant="subtitle2">Authentication</Typography>
      <Typography variant="bodyDefault">
        All requests to this endpoint must be authenticated using Basic Authentication. Use your API
        key as shown in the examples below. For security purposes, you can rotate your API keys at
        any time by visiting the{' '}
        <Link to={apiKeyUrl} target="_blank">
          Settings {'\u2192'} Authentication
        </Link>{' '}
        page.
      </Typography>
      <CodeContainer
        toCopy={`POST ${apiKey.url}`}
        value={`POST ${apiKey.url}`}
        label="RAG Webserver URL"
      />
      <CodeContainer
        toCopy={apiKey.api_key}
        value={`Authorization: Basic ${sanitizedApiKey}`}
        label="Authorization Header"
      />
      <Divider />
      {/* <Typography variant="subtitle2">Payload</Typography>
      <CodeContainer
        toCopy={getCurl(apiKey.api_key, apiKey.url)}
        value={getCurl(sanitizedApiKey, apiKey.url)}
        label="cURL"
      />
      <Divider /> */}
      <Typography variant="subtitle2">Request Payload Schema</Typography>
      <Typography variant="bodyDefault">
        The endpoint accepts JSON payloads with the properties defined in the schema below. Note
        that the {'\u0060'}llm_config{'\u0060'} values depend on your chosen LLM provider. For
        provider-specific payload examples, click the <b>"How to Use"</b> button on the Query Prompt
        card.
      </Typography>
      <Typography variant="bodyDefault" style={{ marginTop: '-8px' }}></Typography>
      <CodeContainer toCopy={payloadSchema} value={payloadSchema} label="" />
    </Stack>
  );
}
